/**
 * The three "standard states" that EVERY data-bearing screen must implement (docs/06 §4):
 * Loading (skeleton matching the final layout — never a bare spinner), Empty (icon + one
 * sentence + a primary action), Error (inline panel with retry).
 *
 * Having them as components rather than as a convention is what makes "every screen implements
 * all three" checkable instead of aspirational.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button, Card, Skeleton, cn } from './primitives';
import { ApiError, NetworkError } from '@/shared/http';

// ── Error ───────────────────────────────────────────────────────────────────────────────────

/**
 * Turns any thrown error into a translated, user-safe sentence.
 *
 * NEVER renders `Problem.detail`: it is English by contract and developer-facing (docs/05). We
 * switch on the stable `type` suffix and translate it ourselves.
 */
export function useErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();

  return React.useCallback(
    (error: unknown): string => {
      if (error instanceof NetworkError) return t('common.errorNetwork');

      if (error instanceof ApiError) {
        const key = `errors.${error.suffix}`;
        const translated = t(key);
        // i18next returns the key itself when it is missing — fall back to a generic sentence
        // rather than showing the user `errors.some-new-problem-type`.
        if (translated !== key) return translated;
        return t('common.errorGeneric');
      }

      return t('common.errorGeneric');
    },
    [t],
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const message = useErrorMessage()(error);

  return (
    <Card className={cn('border-red-200 bg-red-50/50 p-6', className)} role="alert">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
        <div className="flex-1">
          <p className="font-medium text-red-900">{t('common.error')}</p>
          <p className="mt-1 text-sm text-red-800">{message}</p>
          {onRetry ? (
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/** Form-level (non-field) error panel — server errors that map to no single field. */
export function FormErrorPanel({ error }: { error: unknown }) {
  const message = useErrorMessage()(error);
  if (!error) return null;

  return (
    <div
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
      role="alert"
    >
      {message}
    </div>
  );
}

// ── Empty ───────────────────────────────────────────────────────────────────────────────────
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  body?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-input bg-white px-6 py-14 text-center',
        className,
      )}
    >
      {Icon ? <Icon className="mb-3 h-8 w-8 text-muted-foreground/70" aria-hidden /> : null}
      <p className="text-base font-medium text-foreground">{title}</p>
      {body ? <p className="mt-1 max-w-md text-sm text-muted-foreground">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// ── Loading ─────────────────────────────────────────────────────────────────────────────────

/** Skeleton shaped like a table: header row + N body rows of M columns. */
export function TableSkeleton({ rows = 5, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white" aria-busy="true">
      <div className="flex gap-4 border-b border-border bg-muted/60 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-hairline px-4 py-4 last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-4 flex-1', c === 0 && 'max-w-[3rem]')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Skeleton shaped like a stack of form sections. */
export function FormSkeleton({ fields = 6 }: { fields?: number }) {
  return (
    <Card className="p-6" aria-busy="true">
      <Skeleton className="mb-6 h-5 w-40" />
      <div className="grid gap-5 md:grid-cols-2">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function CardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid gap-4 md:grid-cols-3" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="p-5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-16" />
        </Card>
      ))}
    </div>
  );
}
